import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ScrollView, StyleSheet } from 'react-native';
import {
  type CloudinaryTransformConfig,
  FadeView,
  getCloudinaryUrl,
  theme,
  useResponsive,
} from '@sg/garnish';
import { useProductMicroNutritionData } from '@sg/shared-fe/features/menu';
import { ProductDetails } from '@sg/shared-fe/features/product-details';

import { ProductDetailsCarousel } from '@order/features/product-details';
import { useTelemetry } from '@order/Telemetry';

import {
  AppFooter,
  type MenuContentDeliveryDetailsData,
  MenuContentHeader,
  type MenuContentOrderChannel,
  type MenuContentRestaurantData,
} from '../../../../components';
import { useProductDetailsTabs } from '../../hooks';
import { messages } from '../../ProductDetails.messages.ts';
import {
  type BaseProduct,
  type CarouselItems,
  type ProductDetailsMappedContent,
} from '../../ProductDetails.types';
import { ProductNutritionData } from '../ProductNutritionData';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsReadySM = (props: Props) => {
  const {
    baseProduct,
    productCost,
    restaurantData,
    deliveryDetailsData,
    orderChannel,
    locationName,
    content,
    isUnavailableProduct,
    isAddingToBag,
    navigateBack,
    onAddToBag,
    navigateToLocationSearch,
  } = props;

  const hasSuperIngredients = content.superIngredients.length > 0;

  // ─────────────────────────────────────────────────────────────────────

  const { minWidth } = useResponsive();
  const { trackEvent } = useTelemetry();
  const { formatMessage } = useIntl();

  const {
    isNutritionTabActive,
    isSuperIngredientsTabActive,
    nutritionNativeId,
    nutritionPanelNativeId,
    superIngredientsNativeId,
    superIngredientsPanelNativeId,
    setActiveTabToSuperIngredients,
    setActiveTabToNutrition,
  } = useProductDetailsTabs({ hasSuperIngredients });

  // ─── Derived Data ────────────────────────────────────────────────────

  const microNutritionEntries = useProductMicroNutritionData({ baseProduct });

  const carousel = useMemo<CarouselItems>(() => {
    const baseProductImage: CarouselItems[number] | undefined = baseProduct
      ? {
          type: 'image',
          source: getCloudinaryUrl(
            baseProduct?.asset.url,
            BASE_PRODUCT_IMAGE_TRANSFORM_CONFIG,
          ),
          alt: baseProduct.name,
          contentFit: 'contain',
        }
      : undefined;

    return [
      ...(baseProductImage ? [baseProductImage] : []),
      ...content.carousel,
    ];
  }, [baseProduct, content.carousel]);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onContactUs = useCallback(() => {
    trackEvent({ name: 'product-details.contact-us' });
  }, [trackEvent]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <FadeView
      show
      duration={theme.transitions.long}
      style={styles.outerContainer}
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
      >
        {restaurantData ? (
          <MenuContentHeader
            restaurantData={restaurantData}
            deliveryDetailsData={deliveryDetailsData}
            locationName={locationName}
            orderChannel={orderChannel}
            compact
            changeLocation={navigateToLocationSearch}
            onContactUs={onContactUs}
          />
        ) : null}

        <ProductDetails.Container>
          <ProductDetails.Column withBorder centered>
            <ProductDetails.BackButton
              onPress={navigateBack}
              aria-label={formatMessage(messages.goBack)}
            />

            {carousel.length === 0 ? (
              <ProductDetails.Banner.Large
                productName={baseProduct.name}
                productImageUrl={baseProduct.asset.url}
              />
            ) : null}

            {carousel.length > 0 ? (
              <ProductDetailsCarousel items={carousel} />
            ) : null}
          </ProductDetails.Column>

          <ProductDetails.Column>
            <ScrollView
              style={styles.detailsContainer}
              contentContainerStyle={styles.detailsContentContainer}
              showsVerticalScrollIndicator={false}
            >
              <ProductDetails.ContentBlock withBorder>
                {isUnavailableProduct ? (
                  <ProductDetails.UnavailableProduct />
                ) : null}

                <ProductDetails.Header.Large>
                  <ProductDetails.Title color={theme.colors.KALE}>
                    {baseProduct.name}
                  </ProductDetails.Title>

                  {!isUnavailableProduct && minWidth.isMD ? (
                    <ProductDetails.AddToBagButton
                      onPress={onAddToBag}
                      isLoading={isAddingToBag}
                    />
                  ) : null}

                  {isUnavailableProduct && minWidth.isMD ? (
                    <ProductDetails.ChangeLocationButton
                      onPress={navigateToLocationSearch}
                    />
                  ) : null}
                </ProductDetails.Header.Large>

                {content.subheading ? (
                  <ProductDetails.Text color={theme.colors.BLACK}>
                    {content.subheading}
                  </ProductDetails.Text>
                ) : null}

                <ProductDetails.Text color={theme.colors.BLACK}>
                  {content.description ?? baseProduct.description}
                </ProductDetails.Text>

                <ProductDetails.Text color={theme.colors.BLACK}>
                  {typeof productCost === 'number' ? (
                    <>
                      <ProductDetails.Cost costInCents={productCost} />

                      {microNutritionEntries.length > 0 ? (
                        <ProductDetails.TextSeparator />
                      ) : null}
                    </>
                  ) : null}

                  <ProductDetails.NutritionData
                    entries={microNutritionEntries}
                  />
                </ProductDetails.Text>

                {isUnavailableProduct || minWidth.isMD ? null : (
                  <ProductDetails.AddToBagButton
                    onPress={onAddToBag}
                    isLoading={isAddingToBag}
                  />
                )}

                {!isUnavailableProduct || minWidth.isMD ? null : (
                  <ProductDetails.ChangeLocationButton
                    onPress={navigateToLocationSearch}
                  />
                )}
              </ProductDetails.ContentBlock>

              <ProductDetails.ContentBlock>
                <ProductDetails.Tabs.LinksNav>
                  {hasSuperIngredients ? (
                    <ProductDetails.Tabs.Link
                      title={formatMessage(messages.superIngredientsTitle)}
                      active={isSuperIngredientsTabActive}
                      nativeID={superIngredientsNativeId}
                      panelNativeID={superIngredientsPanelNativeId}
                      onPress={setActiveTabToSuperIngredients}
                    />
                  ) : null}

                  <ProductDetails.Tabs.Link
                    title={formatMessage(messages.nutritionInfoTitle)}
                    active={isNutritionTabActive}
                    nativeID={nutritionNativeId}
                    panelNativeID={nutritionPanelNativeId}
                    onPress={setActiveTabToNutrition}
                  />
                </ProductDetails.Tabs.LinksNav>

                {hasSuperIngredients && isSuperIngredientsTabActive ? (
                  <ProductDetails.Tabs.Tab
                    nativeID={superIngredientsPanelNativeId}
                    tabLinkID={superIngredientsNativeId}
                  >
                    <ProductDetails.IngredientsStack>
                      {content.superIngredients.map((entry) => (
                        <ProductDetails.Ingredient.Container key={entry.name}>
                          <ProductDetails.Ingredient.Image
                            alt={entry.name}
                            baseUrl={entry.image.fields.file.url}
                          />

                          <ProductDetails.Ingredient.ContentContainer>
                            <ProductDetails.Ingredient.Title>
                              {entry.name}
                            </ProductDetails.Ingredient.Title>

                            <ProductDetails.Ingredient.Highlights
                              highlights={entry.highlights}
                            />
                          </ProductDetails.Ingredient.ContentContainer>
                        </ProductDetails.Ingredient.Container>
                      ))}
                    </ProductDetails.IngredientsStack>
                  </ProductDetails.Tabs.Tab>
                ) : null}

                {isNutritionTabActive ? (
                  <ProductDetails.Tabs.Tab
                    nativeID={nutritionPanelNativeId}
                    tabLinkID={nutritionNativeId}
                  >
                    <ProductNutritionData baseProduct={baseProduct} />
                  </ProductDetails.Tabs.Tab>
                ) : null}
              </ProductDetails.ContentBlock>
            </ScrollView>
          </ProductDetails.Column>
        </ProductDetails.Container>

        <AppFooter />
      </ScrollView>
    </FadeView>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const BASE_PRODUCT_IMAGE_TRANSFORM_CONFIG: CloudinaryTransformConfig = [
  { crop: 'crop', height: 0.95, width: 0.7 },
  { width: 1000 },
];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  contentContainer: {
    flex: 1,
  },
  detailsContainer: {
    minHeight: '100%',
  },
  detailsContentContainer: {
    flexGrow: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  baseProduct: BaseProduct;
  productCost: number | undefined;
  restaurantData: MenuContentRestaurantData | null | undefined;
  deliveryDetailsData: MenuContentDeliveryDetailsData | undefined | null;
  orderChannel: MenuContentOrderChannel;
  locationName: string;
  content: ProductDetailsMappedContent;
  isUnavailableProduct: boolean;
  isAddingToBag: boolean;
  navigateBack: () => void;
  navigateToLocationSearch: () => void;
  onAddToBag: () => void;
};
